import type moment from 'moment'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import LineCommentIcon from 'LuxKit/Icons/line/LineCommentIcon'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineSettingIcon from 'LuxKit/Icons/line/LineSettingIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import { getCurrentUserId } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { AddRemoveItineraryButton } from 'tripPlanner/components/Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from 'tripPlanner/components/Itinerary/ItineraryDateButton'
import DeleteTripItem from 'tripPlanner/components/TripModal/TripItems/DeleteTripItem'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { ItemMetadata } from 'tripPlanner/models'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { getOrderId, isCustomItem, buildMyEscapesOrderLink } from 'tripPlanner/utils'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { canSetItemStartDateFromNoDates } from 'tripPlanner/utils/itinerary'
import { mediaQueryDown } from 'components/utils/breakpoint'
import Divider from 'LuxKit/Divider'
import GroupWithSeparator from 'tripPlanner/components/CleanCard/common/GroupWithSeparator'
import useDesktopWithoutTouch from 'tripPlanner/hooks/useDesktopWithoutTouch'

const FullWidthWrapper = styled.div`
  button {
    width: 100%;
    justify-content: flex-start;
  }
`

const MobileOnlyDivider = styled(Divider)`
  display: none;

  ${mediaQueryDown.mobile} {
    display: block;
  }
`

interface Props {
  item: TripItem
  offerLink?: string
  onRequestAddComment?: () => void
}

export default function ItemMenuOptions(props: Props) {
  const { item, onRequestAddComment, offerLink } = props
  const intl = useIntl()
  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()
  const navigateTo = useNavigateTo()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const canEdit = useCanEditTrip()

  const canShowEdit = isCustomItem(item) && ItemMetadata[item.type].editable

  const orderId = getOrderId(item)
  const userId = useAppSelector(getCurrentUserId)

  const documentsContext = useContext(TripDocumentsContext)

  const canSetDatesFromNoDates = canSetItemStartDateFromNoDates(item)
  const isDesktopWithoutTouch = useDesktopWithoutTouch()

  const addNoteLabel = intl.formatMessage({
    id: 'ZzQizkeNGgS26mnt5aTF',
    defaultMessage: 'Add a note',
    description: 'Menu option to add a note to a trip note item in the trip planner',
  })
  const addCommentLabel = intl.formatMessage({
    id: 'kiZOwFT7ccYtFnQcKnN-',
    defaultMessage: 'Add a comment',
    description: 'Menu option to add a comment to a trip item in the trip planner',
  })

  const onClickMoveTo = useCallback(() => {
    const onRequestAddTripDates = (
      onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
    ) => {
      openModal(EditTripDates, { tripId: item.tripId, onDatesAdded })
    }

    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
    } else {
      onRequestAddTripDates()
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const onClickAddToItinerary = useCallback(() => {
    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const handleDeleteItem = useCallback(() => {
    openModal(DeleteTripItem, { item }, { height: 'auto' })
  }, [openModal, item])

  const handleClickAddComment = useCallback(() => {
    closeModal()
    requestAnimationFrame(() => {
      if (onRequestAddComment) {
        onRequestAddComment()
      }
    })
  }, [closeModal, onRequestAddComment])

  const handleClickEdit = useCallback(() => openModal(EditTripItem, { item }), [openModal, item])
  const handleClickAddDocument = useCallback(() => documentsContext?.onRequestAddDocument(item.id), [documentsContext, item.id])

  const isBooked = 'isBooked' in item && item.isBooked

  const myEscapesLink = buildMyEscapesOrderLink(item)

  if (!canEdit) {
    return null
  }

  return (
    <GroupWithSeparator direction="vertical" separator={<MobileOnlyDivider kind="secondary" direction="horizontal" />}>
      <div>
        {canSetDatesFromNoDates && (
          <FullWidthWrapper>
            <ItineraryDateButton
              item={item}
              variant="button"
              onRequestAddTripDates={onClickMoveTo}
            />
          </FullWidthWrapper>
        )}
        {!canSetDatesFromNoDates && (
          <FullWidthWrapper>
            <AddRemoveItineraryButton
              item={item}
              variant="button"
              onClickAddTripItemDates={onClickAddToItinerary}
            />
          </FullWidthWrapper>
        )}

        {orderId && item.createdBy === userId && (
          <ClickableListItem
            startIcon={<LineSettingIcon size="S" />}
            title={intl.formatMessage({
              id: 'ErN-7p4yv6hyy2BGS3MI',
              defaultMessage: 'Manage booking',
              description: 'Menu option to navigate to booking management page',
            })}
            to={myEscapesLink}
            target="_blank"
          />
        )}
      </div>

      <div>
        {canShowEdit && (
          <ClickableListItem
            startIcon={<LinePenIcon size="S" />}
            onClick={handleClickEdit}
            title={intl.formatMessage({
              id: 'm9vEjbRjVQYGsQ7Ugk4u',
              defaultMessage: 'Edit',
              description: 'Menu option to edit a custom trip item in the trip planner feature',
            })}
          />
        )}
      </div>

      <div>
        {!!onRequestAddComment && !item.notes && (
          <ClickableListItem
            startIcon={<LineCommentIcon size="S" />}
            onClick={handleClickAddComment}
            title={item.type === 'NOTE' ? addNoteLabel : addCommentLabel}
          />
        )}
        {!!documentsContext && (
          <ClickableListItem
            startIcon={<LinePaperclipIcon size="S" />}
            onClick={handleClickAddDocument}
            title={intl.formatMessage({
              id: 'pn_mYlBmlV6eDg0R1uX5',
              defaultMessage: 'Attach a document',
              description: 'Menu option to attach a document to a trip item in the trip planner',
            })}
          />
        )}
      </div>
      {(isBooked || !isDesktopWithoutTouch) && (
        <ClickableListItem
          startIcon={<LineTrashAltIcon size="S" colour="critical" />}
          onClick={handleDeleteItem}
          title={intl.formatMessage({
            id: 'vANP4wygHGEwm5rY1p4Q',
            defaultMessage: 'Delete from trip',
            description: 'Menu option to delete an item from the trip in the trip planner',
          })}
          titleColour="critical"
        />
      )}
    </GroupWithSeparator>
  )
}
